/* Global import */
import {action, computed, decorate, observable} from 'mobx';
/* Custom import */
import Api from '../util/api'

export default class AuthStore {
    isLogged = false;
    isLoggedDash = false;
    isLoggedEcho = false;

    error = '';
    loading = false;
    checkLoading = false;
    user = observable(
        {
            // observable properties:
            id: "",
            prenom: "",
            nom: ""
        });

    nameCompany;
    complement_adresse;
    postcode;
    telCompany;
    emailCompany;
    typeCompany;
    identifiant;
    website;
    color;
    city;
    horaires;
    logo;
    sidebar;
    // Réglages de l'écran client « manette » : zone morte en % du centre, zoom des mires en ×N
    stickZoneMorte = 5;
    stickZoom = 5;
    // Fond du bandeau d'Aerith : rotation automatique toutes les N minutes, ou choix manuel jusqu'à fondExpireA (ms)
    fondRotationMinutes = 10;
    fondChoisi = null;
    fondExpireA = 0;
    // Images de la galerie associées à chaque fond : {identifiant du fond : [{id, asset_id, url, nom}]}
    fondImages = {};
    // Réglages propres à chaque fond : {identifiant du fond : {réglage : valeur}}, seulement les valeurs modifiées
    fondReglages = {};
    // fonds retirés de la rotation automatique
    fondExclus = [];
    // faux : plus de rotation, le fond choisi à la main reste sans limite de durée
    fondRotationActive = true;
    // heures (0 à 23) à partir desquelles l'ambiance automatique des fonds est jour, crépuscule puis nuit
    fondHoraires = {jour: 9, crepuscule: 16, nuit: 18};
    // Diaporama du mode Publicité : {diapositives: [...]} (voir views/screen/publicite/diaporama.js)
    publicite = {diapositives: []};
    options = observable([]);
    modules = observable([]);
    // droits du compte (voir config/droits.php de l'API) : un administrateur les a tous
    droits = [];

    constructor(rootStore) {
        this.rootStore = rootStore;
        this.api = new Api();
        this.checkIsLogged();
    }

    get username() {
        return this.user.fullname;
    }

    get estAdmin() {
        return this.user?.role === "admin";
    }

    aLeDroit(droit) {
        return this.estAdmin || (this.droits || []).includes(droit);
    }

    /** Page ou entrée du menu permise à ce compte : `droit: "chiffres"` ou `admin: true` (routes.js, _nav.js). */
    autorise(element) {
        return (!element.droit || this.aLeDroit(element.droit)) && (!element.admin || this.estAdmin);
    }

    async loginUrl(url) {
        this.clearError();
        try {
            const authResponse = await this.api.auth.loginUrl(url);
            localStorage.setItem('token', authResponse.token);
            // l'API répond {token, data} : le logo est dans data (lire authResponse.logo l'effaçait)
            if (authResponse.data?.logo) this.logo = authResponse.data.logo;
            this.setReglagesManette(authResponse.data?.reglages_manette);
            this.setReglagesFond(authResponse.data?.reglages_fond);
            this.setPublicite(authResponse.data?.publicite);
            this.watchSession(authResponse.token)
            this.isLoggedDash = true;

            return authResponse
        } catch (error) {
            this.setError(error);
        }
    }

    async login(email, password) {
        this.loading = true;
        this.clearError();

        try {
            const authResponse = await this.api.auth.login(email, password);
            localStorage.setItem('token', authResponse.token);
            localStorage.setItem('userId', authResponse.data.user.id);
            this.user = authResponse.data.user;
            this.setLogged(authResponse);

            this.rootStore.getEcho().connect()

        } catch (error) {
            this.setError(error);
        }
    }

    async findAdmin() {
        try {
            let id = localStorage.getItem("userId");
            this.user = await this.api.users.findUserById(id);
        } catch (e) {
        }

    }

    async logout() {
        this.clearError();
        this.isLogged = false;
        this.isLoggedDash = false;
        localStorage.removeItem('token');
    }

    async checkIsLogged() {
        this.checkLoading = true;
        this.clearError();
        try {
            const token = window.localStorage.getItem('token');
            const authResponse = await this.api.auth.checkIsLoged(token || '');
            this.setLogged(authResponse);
        } catch (error) {
            this.setError(error);
        }
    }

    async updateLogo(datas) {
        this.rootStore.savedDatasStore.setLoading(true)
        let authResponse = await this.api.auth.updateLogo(datas);

        if (authResponse?.status === "success") {
            this.rootStore.notificationStore.setNotification("Logo modifié", "success");
            this.logo = authResponse.data
        }

        this.rootStore.savedDatasStore.setLoading(false)
        return authResponse
    }

    async updateSidebar(datas) {
        this.rootStore.savedDatasStore.setLoading(true)
        let authResponse = await this.api.auth.updateSidebar(datas);

        if (authResponse?.status === "success") {
            this.rootStore.notificationStore.setNotification("Sidebar modifiée", "success");
            this.sidebar = authResponse.data
        }

        this.rootStore.savedDatasStore.setLoading(false)
        return authResponse
    }

    async updateDetails(datas) {
        this.rootStore.savedDatasStore.setLoading(true)

        try {
            let authResponse = await this.api.auth.updateDetails(datas);

            if (authResponse?.status === "success") {
                this.rootStore.notificationStore.setNotification("Paramètres entreprise modifiés", "success");
                this.setDatas(authResponse.data)
            }

            return authResponse
        } finally {
            // sans cela, un refus du serveur laissait le chargement affiché indéfiniment
            this.rootStore.savedDatasStore.setLoading(false)
        }
    }


    setLogged(authResponse) {
        this.isLogged = true;
        this.loading = false;
        this.checkLoading = false;
        this.rootStore.userStore.setUser(authResponse.data.user);
        this.user = authResponse.data.user
        this.modules = authResponse.data.modules
        this.droits = authResponse.data.droits || []
        this.options = authResponse.data.options

        this.color =  this.options.color
        this.sidebar =  this.options.sidebar

        this.setDatas(authResponse.data.data)
        this.watchSession(authResponse.token)
    }

    /**
     * Fond du bandeau, tel que le serveur le décrit : {rotation_minutes, choisi, reste_secondes}.
     * Le serveur donne une durée restante et non une heure : on la compte avec l'horloge de cet écran.
     */
    setReglagesFond(reglages) {
        if (!reglages) return;
        this.fondRotationMinutes = Number(reglages.rotation_minutes) || 10;
        this.fondChoisi = reglages.choisi || null;
        this.fondExpireA = reglages.choisi ? Date.now() + Number(reglages.reste_secondes) * 1000 : 0;
        // PHP envoie [] (et non {}) quand il n'y a aucune image
        this.fondImages = reglages.images && !Array.isArray(reglages.images) ? reglages.images : {};
        this.fondReglages = reglages.reglages && !Array.isArray(reglages.reglages) ? reglages.reglages : {};
        this.fondExclus = Array.isArray(reglages.exclus) ? reglages.exclus : [];
        this.fondRotationActive = reglages.rotation_active !== false;
        if (reglages.horaires_ambiance) this.fondHoraires = {...reglages.horaires_ambiance};
    }

    /** Ajoute (assetId) ou retire (imageId) une image d'un fond ; l'écran client change en direct. */
    /** Enregistre les réglages d'un fond (null : valeurs d'origine) ; l'écran client change en direct. */
    async enregistrerReglagesFond(fond, reglages) {
        const reponse = await this.api.auth.updateFondReglages(fond, reglages);
        if (reponse?.status === "success") this.setReglagesFond(reponse.data);
        return reponse
    }

    /** Enregistre les fonds retirés de la rotation automatique ; l'écran client change en direct. */
    async enregistrerFondsExclus(exclus) {
        const reponse = await this.api.auth.updateFondRotation(exclus);
        if (reponse?.status === "success") this.setReglagesFond(reponse.data);
        return reponse
    }

    async ajouterImageFond(fond, assetId) {
        const reponse = await this.api.auth.addFondImage(fond, assetId);
        if (reponse?.status === "success") this.setReglagesFond(reponse.data);
        return reponse
    }

    async retirerImageFond(imageId) {
        const reponse = await this.api.auth.removeFondImage(imageId);
        if (reponse?.status === "success") this.setReglagesFond(reponse.data);
        return reponse
    }

    async choisirFond(fond) {
        this.rootStore.savedDatasStore.setLoading(true)
        try {
            const reponse = await this.api.auth.updateFond(fond);
            if (reponse?.status === "success") {
                this.setReglagesFond(reponse.data);
                this.rootStore.notificationStore.setNotification(fond ? "Fond du bandeau modifié" : "Retour à la rotation automatique", "success");
            }
            return reponse
        } finally {
            this.rootStore.savedDatasStore.setLoading(false)
        }
    }

    /** Diaporama du mode Publicité, tel que le serveur l'envoie (adresses des images et vidéos comprises). */
    setPublicite(publicite) {
        if (!publicite || !Array.isArray(publicite.diapositives)) return;
        this.publicite = {diapositives: publicite.diapositives};
    }

    /** Enregistre le diaporama du mode Publicité ; l'écran client change en direct. */
    async enregistrerPublicite(diapositives) {
        const reponse = await this.api.auth.updatePublicite(diapositives);
        if (reponse?.status === "success") this.setPublicite(reponse.data);
        return reponse
    }

    setReglagesManette(reglages) {
        if (!reglages) return;
        this.stickZoneMorte = Number(reglages.zone_morte);
        this.stickZoom = Number(reglages.zoom);
    }

    setDatas(data) {
        this.nameCompany = data.name
        this.complement_adresse = data.complement_adresse
        this.postcode = data.postcode
        this.adresse = data.adresse
        this.typeCompany = data.type
        this.telCompany = data.tel
        this.emailCompany = data.email
        this.website = data.website
        this.city = data.city
        this.identifiant = data.identifiant
        this.logo = data.logo
        this.horaires = data.horaires
        this.setReglagesManette({zone_morte: data.stick_zone_morte ?? 5, zoom: data.stick_zoom ?? 5})
        this.setReglagesFond(data.reglages_fond)
        this.setPublicite(data.publicite_diaporama)
    }

    isMicro() {
        return this.typeCompany === "micro";
    }

    async refreshToken() {
        try {
            let authResponse = await this.api.auth.refreshToken();
            localStorage.removeItem('token');
            localStorage.setItem('token', authResponse.token);
        } catch (e) {
        }
    }

    setError(error) {
        this.loading = false;
        this.checkLoading = false;
        this.isLogged = false;
        this.isLoggedDash = false;
        this.error = error || '';
        localStorage.removeItem('token')
    }


    watchSession(token) {
        setInterval(() => {
            this.refreshToken(token)
        }, 14400000)
    }

    clearError() {
        this.error = '';
    }
}

decorate(AuthStore, {
    clearError: action,
    login: action,
    watchSession: action,
    setLogged: action,
    setError: action,
    checkIsLogged: action,
    logout: action,
    checkLoading: observable,
    isLogged: observable,
    isLoggedDash: observable,
    isLoggedEcho: observable,
    error: observable,
    loading: observable,
    user: observable,
    username: computed,
    nameCompany: observable,
    complement_adresse: observable,
    postcode: observable,
    telCompany: observable,
    emailCompany: observable,
    typeCompany: observable,
    logo: observable,
    sidebar: observable,
    modules: observable,
    droits: observable,
    estAdmin: computed,
    home: observable,
    identifiant: observable,
    options: observable,
    stickZoneMorte: observable,
    stickZoom: observable,
    setReglagesManette: action,
    setReglagesFond: action,
    fondRotationMinutes: observable,
    fondChoisi: observable,
    fondExpireA: observable,
    fondImages: observable,
    fondReglages: observable,
    fondExclus: observable,
    fondRotationActive: observable,
    fondHoraires: observable,
    enregistrerReglagesFond: action,
    ajouterImageFond: action,
    retirerImageFond: action,
    publicite: observable,
    setPublicite: action,
    enregistrerPublicite: action
});